/**
 * EmbeddedTicketDialog - Jira-style modal that renders the ticket-creation form
 * inside the Work Order create page for a single, fixed Thaklis service.
 *
 * The service is locked (chosen from the WO line item), the employee search is
 * pre-filled with the customer's name, and on successful creation the created
 * ticket is handed back to the parent (which auto-links it to the WO line).
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Chip, IconButton,
  CircularProgress, Alert, useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  ConfirmationNumber as TicketIcon,
  Link as LinkIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { FieldStructureProvider } from './FieldStructureContext';
import { TicketForm } from './index';
import ThaklisApiService from '../../services/thaklis-api.service';
import { getBrandContrastText, getBrandGradient } from '../../utils/colorUtils';

const EmbeddedTicketDialog = ({
  open, onClose, mappingId, serviceId, serviceName, orgName, prefillName, onCreated
}) => {
  const theme = useTheme();
  const [initializing, setInitializing] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const blocking = initializing || busy || finalizing;

  // Prevent closing (backdrop / Esc / X) while a request is in flight.
  const handleClose = (event, reason) => {
    if (blocking) return;
    if (onClose) onClose(event, reason);
  };

  // Keep the popup loader up through the host's post-create work too. TicketForm turns
  // `busy` off as soon as the ticket POST resolves, but the WO page then re-fetches the
  // ticket (for the link snapshot) and closes the dialog — cover that tail with `finalizing`.
  const handleCreated = async (ticket) => {
    setFinalizing(true);
    try {
      await onCreated?.(ticket);
    } catch (err) {
      console.error('Post-create handling failed:', err);
    } finally {
      setFinalizing(false);
    }
  };

  // Initialize the Thaklis client for this organization mapping each time the
  // dialog opens (the mapping/org may differ between line items).
  useEffect(() => {
    if (!open || !mappingId) return undefined;
    let active = true;
    setReady(false);
    setError(null);
    setInitializing(true);
    (async () => {
      try {
        await ThaklisApiService.initForOrganization(mappingId);
        if (active) setReady(true);
      } catch (err) {
        console.error('Failed to initialize Thaklis API for ticket dialog:', err);
        if (active) {
          setError('Failed to connect to Thaklis for this organization. Please ensure valid credentials are configured.');
        }
      } finally {
        if (active) setInitializing(false);
      }
    })();
    return () => { active = false; };
  }, [open, mappingId]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { position: 'relative', borderRadius: 3, maxHeight: '92vh', overflow: 'hidden' } }}
    >
      {/* Jira-style header */}
      <Box
        sx={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 2, px: 3, py: 2,
          background: getBrandGradient(theme),
          color: getBrandContrastText(theme)
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TicketIcon sx={{ fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Create Ticket</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {serviceName && (
              <Chip
                label={serviceName}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'inherit', fontWeight: 600 }}
              />
            )}
            {orgName && (
              <Chip
                icon={<BusinessIcon sx={{ fontSize: '0.95rem !important', color: 'inherit !important' }} />}
                label={orgName}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'inherit', fontWeight: 600 }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.9 }}>
              <LinkIcon sx={{ fontSize: 15 }} />
              <Typography sx={{ fontSize: '0.72rem' }}>
                Auto-links to this work-order line on creation
              </Typography>
            </Box>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={blocking} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: { xs: 1.5, sm: 2.5 } }}>
        {initializing && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">Connecting to Thaklis…</Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {ready && (
          <FieldStructureProvider>
            <TicketForm
              organizationId={mappingId}
              organizationName={serviceName}
              embedded
              lockedServiceId={serviceId}
              lockedServiceName={serviceName}
              prefillEmployeeName={prefillName}
              onCreated={handleCreated}
              onBusyChange={setBusy}
            />
          </FieldStructureProvider>
        )}
      </DialogContent>

      {/* Popup-scoped loading overlay — covers only this dialog (not the whole page)
          while a request is in flight, and swallows clicks so the form can't be
          re-submitted mid-request. */}
      {(busy || finalizing) && (
        <Box
          sx={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.6)'
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Dialog>
  );
};

export default EmbeddedTicketDialog;
